/**
 * DL-63086 epic (Supplier contact column, Testmo folder 101443) — DL-63094 (Testmo #888041,
 * filter apply/clear/combine) and DL-63101 (Testmo #888044, table global search) both need a
 * handful of Mapping Request rows with distinct, known `supplierContactPerson` values so the
 * filter/search mock logic in `mocks/api/handlers/purchaseOrders.ts` has something real to
 * narrow — `generatePurchaseOrders`'s default seed never sets this field at all (see DL-63091's
 * PR, which found the same gap for the column's own render tests).
 *
 * Deliberately NOT reusing `mocks/data/purchaseOrderFilterVolume.ts` (epic DL-62980,
 * `claude/dl-62980-mapping-request-filter-search`, unmerged at the time this file was added) —
 * that PR is still in flight and this epic's own instruction (DL-63094/DL-63096) is to build
 * against current `develop`, not an unmerged sibling branch. This file's ids/names/refNumbers are
 * chosen not to collide with either that PR's fixtures (90001-90005, "Zophia Quintrell" et al.) or
 * DL-63091's merged probe rows (8801-8803, "Priya Kapoor"). Expect a small, easy, additive merge
 * conflict with DL-62980's own `purchaseOrders.ts` changes if/when it lands — both add a
 * `supplierContactPerson` filter clause and an org-scoped fixture-row append in the same handler;
 * whoever merges second resolves it (two independent, non-overlapping id ranges, not a logic
 * conflict).
 *
 * Organization-scoped to id 2 (the default designer demo org — confirmed via
 * `frontend/e2e/src/fixtures/index.ts`'s login user, `organizationId: 2`), mirroring how
 * `purchaseOrders.ts` already caches PO data per org.
 */

export const MAPPING_REQUEST_CONTACT_FIXTURE_ORG_ID = 2;

export const MAPPING_REQUEST_CONTACTS = {
  alpha: {
    id: 'mrc-alpha-9781',
    name: 'Ingrid Voss',
    email: 'ingrid.voss@mapping-request-contact-probe.test',
    avatarColor: '0B7285',
  },
  beta: {
    id: 'mrc-beta-9782',
    name: 'Tobias Renner',
    email: 'tobias.renner@mapping-request-contact-probe.test',
    avatarColor: 'C2255C',
  },
  gamma: {
    id: 'mrc-gamma-9783',
    name: 'Wren Okafor',
    email: 'wren.okafor@mapping-request-contact-probe.test',
    avatarColor: '5C940D',
  },
} as const;

type FixtureContact = (typeof MAPPING_REQUEST_CONTACTS)[keyof typeof MAPPING_REQUEST_CONTACTS];

/** Mirrors `purchaseOrderStatuses`' shape for the two Pending-tab statuses these rows use. */
const STATUS_REQUESTED = {
  id: 3,
  name: 'Requested',
  localeCode: 'postate_requested',
  visibilityStatus: 'Published',
  poMappingRequestTab: 'Pending',
};
const STATUS_ON_HOLD = {
  id: 7,
  name: 'On hold',
  localeCode: 'postate_on_hold',
  visibilityStatus: 'On Hold',
  poMappingRequestTab: 'Pending',
};

const buildFixturePo = (
  id: number,
  name: string,
  status: typeof STATUS_REQUESTED | typeof STATUS_ON_HOLD,
  contact: FixtureContact | undefined
) => ({
  id: String(id),
  organizationId: MAPPING_REQUEST_CONTACT_FIXTURE_ORG_ID,
  refNumber: `PO-MRC-${String(id).slice(-3)}`,
  name,
  createdByUser: {
    id: String(id),
    name: 'Jamie Rivers',
    email: 'jamie.rivers@example.com',
    avatarColor: '4C6EF5',
  },
  supplier: { id, name: 'Acme Textiles', isActive: true },
  company: { id, name: 'Northwind Apparel', isActive: true },
  status,
  createdAt: '2026-08-01T09:00:00.000Z',
  updatedAt: '2026-08-01T09:00:00.000Z',
  publishedAt: '2026-08-01T09:00:00.000Z',
  stylesCount: 3,
  itemsCount: 5,
  supplierContactPerson: contact,
});

/**
 * Four deterministic rows: two share `alpha` under different statuses (proves AND-narrowing when
 * a second filter is combined, and that a status-only difference is enough to exclude one of
 * them), one each of `beta`/`gamma` (proves a contact filter excludes rows with a *different*
 * assigned contact, and gives Testmo #888044's global search three distinct, distinguishable
 * names to search across).
 */
export const MAPPING_REQUEST_CONTACT_FIXTURE_ROWS = [
  buildFixturePo(
    97801,
    'Mapping request contact probe - alpha requested',
    STATUS_REQUESTED,
    MAPPING_REQUEST_CONTACTS.alpha
  ),
  buildFixturePo(
    97802,
    'Mapping request contact probe - alpha on hold',
    STATUS_ON_HOLD,
    MAPPING_REQUEST_CONTACTS.alpha
  ),
  buildFixturePo(
    97803,
    'Mapping request contact probe - beta requested',
    STATUS_REQUESTED,
    MAPPING_REQUEST_CONTACTS.beta
  ),
  buildFixturePo(
    97804,
    'Mapping request contact probe - gamma requested',
    STATUS_REQUESTED,
    MAPPING_REQUEST_CONTACTS.gamma
  ),
];
